/* eslint-disable no-lone-blocks */
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import RoundButton from '@oberoninternal/travelbase-ds/components/action/RoundButton';
import ArrowLeft from '@oberoninternal/travelbase-ds/components/figure/ArrowLeft';
import ArrowRight from '@oberoninternal/travelbase-ds/components/figure/ArrowRight';
import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box, Flex } from '@rebass/grid';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isSameWeek from 'date-fns/isSameWeek';
import isValid from 'date-fns/isValid';
import { useField } from 'formik';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedDate, FormattedDateTimeRange, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import activitySearchParams from '../constants/activitySearchParams';
import parseToDateString from '../constants/parseToDateString';
import { Locale } from '../entities/Locale';
import useDatepicker from '../hooks/useDatepicker';
import useQueryParams from '../hooks/useQueryParams';
import isDate from '../utils/isDate';
import { getDateFnsLocale } from '../utils/localeHelpers';
import parseDate from '../utils/parseDate';
import { convertToPeriod } from '../utils/trip';
import { createDayModifier } from './designsystem/Datepicker';
import DatepickerContext from './designsystem/datepicker/Context';
import DatepickerModal from './designsystem/datepicker/DatepickerModal';
import { StyledViewToggle } from './designsystem/ViewToggle';
import Stack from './Stack';

export const weekOpts = { weekStartsOn: 1 } as const;

export type AgendaView = 'month' | 'day' | 'week';

const isOrBeforeToday = (date: Date, excludeToday = false) => {
    const today = new Date();
    const sameDay = isSameDay(date, today);
    const beforeToday = isBefore(date, today);

    if (excludeToday) {
        return !sameDay && beforeToday;
    }

    return sameDay || beforeToday;
};

interface Props {
    onViewChange: (newView: AgendaView) => void;
    resetDate: () => void;
}

const AgendaHeader: FC<React.PropsWithChildren<Props>> = ({ onViewChange, resetDate }) => {
    const [{ value: startDate }] = useField<string>('startDate');
    const [{ value: endDate }] = useField<string>('endDate');
    const [{ value: view }] = useField<AgendaView>('view');
    const [, setParams] = useQueryParams(activitySearchParams);

    const datepicker = useDatepicker(
        {
            focus: 'start',
            autoHide: false,
            optionalSingleDate: true,
            period: {
                start: new Date(startDate),
                end: new Date(endDate),
            },
        },
        false
    );

    const [state, { sesame, set }] = datepicker;
    const resetWeek = useCallback(() => {
        onViewChange('week');
    }, [onViewChange]);
    const router = useRouter();

    const dateLocale = getDateFnsLocale(router.locale as Locale);
    const computeDayProps = useMemo(() => createDayModifier(state), [state]);
    const onPeriodClick = useCallback(
        (prev = false) => {
            const currentDate = new Date(startDate);
            const periodAdapter = (addFn: typeof addMonths) => addFn(currentDate, prev ? -1 : 1);

            switch (view) {
                case 'month':
                    {
                        let newDate = periodAdapter(addMonths);
                        if (isOrBeforeToday(newDate)) {
                            if (isSameMonth(newDate, new Date())) {
                                newDate = new Date();
                            } else {
                                return;
                            }
                        }
                        setParams({ startDate: parseToDateString(newDate), endDate: parseToDateString(newDate) });
                    }
                    break;
                case 'week':
                    {
                        let newDate = periodAdapter(addWeeks);
                        if (isOrBeforeToday(newDate)) {
                            if (isSameWeek(newDate, new Date(), weekOpts)) {
                                newDate = new Date();
                            } else {
                                return;
                            }
                        }
                        setParams({ startDate: parseToDateString(newDate), endDate: parseToDateString(newDate) });
                    }
                    break;
                case 'day':
                    {
                        const newDate = periodAdapter(addDays);
                        if (isOrBeforeToday(newDate, true)) {
                            return;
                        }
                        setParams({ startDate: parseToDateString(newDate), endDate: parseToDateString(newDate) });
                    }
                    break;

                default:
                    break;
            }
        },
        [startDate, setParams, view]
    );
    useEffect(() => {
        set({ period: convertToPeriod(startDate, endDate) });
    }, [startDate, endDate, set]);
    return (
        <DatepickerContext value={{ computeDayProps, datepicker, locale: dateLocale }}>
            <Header>
                <Navigation>
                    <div className="lt-m" style={{ flex: 1 }}>
                        <MobileSearchBox onClick={() => sesame('open')}>
                            <StyledText>
                                {isValid(new Date(startDate)) && (
                                    <FormattedDate value={startDate} day="numeric" month="short" />
                                )}
                                {isValid(new Date(endDate)) && (
                                    <>
                                        {' — '}
                                        <FormattedDate value={endDate} day="numeric" month="short" />
                                    </>
                                )}
                            </StyledText>
                            {startDate && endDate ? (
                                <StyledDatePickerEditText>
                                    <FormattedMessage defaultMessage="Bewerken" />
                                </StyledDatePickerEditText>
                            ) : (
                                <StyledDatePickerText>
                                    <FormattedMessage defaultMessage="Kies datum" />
                                </StyledDatePickerText>
                            )}
                        </MobileSearchBox>

                        <DatepickerModal
                            hideHelper={false}
                            helperChildren={
                                <Flex mt={3} width={1} flexDirection="row" justifyContent="space-between">
                                    <Button
                                        onClick={() => {
                                            resetDate();
                                            set({ period: null });
                                        }}
                                        variant="outline"
                                    >
                                        <FormattedMessage defaultMessage="Reset datum" />
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            sesame('close');
                                            if (!isDate(state.period) && state.period?.start && state.period?.end) {
                                                setParams(
                                                    {
                                                        startDate: parseToDateString(new Date(state.period.start)),
                                                        endDate: parseToDateString(new Date(state.period.end)),
                                                        view: null,
                                                    },
                                                    'replaceIn'
                                                );
                                            }
                                        }}
                                    >
                                        <FormattedMessage defaultMessage="Zoek activiteiten" />
                                    </Button>
                                </Flex>
                            }
                        />
                    </div>
                    <StyledButton className="gt-s" variant="outline" onClick={() => resetDate()}>
                        <FormattedMessage defaultMessage="Vandaag" />
                    </StyledButton>
                    <div className="gt-m">
                        <Title style={{ textTransform: view === 'month' ? 'capitalize' : undefined }}>
                            {view === 'day' && startDate === endDate ? (
                                <FormattedDate value={new Date(startDate)} format="DAYMONTHYEAR" />
                            ) : (
                                <FormattedDateTimeRange
                                    from={parseDate(startDate)}
                                    to={parseDate(endDate)}
                                    day="numeric"
                                    month="long"
                                    year="numeric"
                                />
                            )}

                            {view === 'month' && <FormattedDate value={new Date(startDate)} format="MONTH" />}
                        </Title>
                    </div>
                    <Stack className="gt-m" variant="inline" spacing={3}>
                        <RoundButton onClick={() => onPeriodClick(true)} variant="outline" size="small">
                            <ArrowLeft />
                        </RoundButton>
                        <RoundButton onClick={() => onPeriodClick()} variant="outline" size="small">
                            <ArrowRight />
                        </RoundButton>
                    </Stack>
                </Navigation>

                <Flex
                    className="lt-s"
                    width="100%"
                    mt={4}
                    mb={2}
                    justifyContent="left"
                    flex={1}
                    flexDirection={['row', 'column']}
                >
                    <StyledLabel>periode</StyledLabel>
                </Flex>
                <Flex
                    className="lt-s"
                    width="100%"
                    flexDirection="row"
                    justifyContent="flex-start"
                    flex={1}
                    alignItems="center"
                >
                    <Box mr={3}>
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetDate();
                            }}
                        >
                            <FormattedMessage defaultMessage="Vandaag" />
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetWeek();
                            }}
                        >
                            <FormattedMessage defaultMessage="Deze week" />
                        </Button>
                    </Box>
                </Flex>
            </Header>
        </DatepickerContext>
    );
};

export default AgendaHeader;
const MobileSearchBox = styled(Flex).attrs({
    justifyContent: 'space-between',
    alignItems: 'center',
})`
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: '100%';
        max-width: 36rem;
    }

    height: 4.8rem;
    border: 1px solid ${({ theme }) => theme.colors.neutral['20']};
    background: ${({ theme }) => theme.colors.neutral[0]};
    box-shadow: 0px 0.2rem 0.4rem 0px rgba(59, 118, 160, 0.02), 0px 0.4rem 1.2rem 0px rgba(59, 118, 160, 0.04);
    border-radius: 2.4rem;
    padding: 1.6rem;
    cursor: pointer;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        font-size: 1.6rem;
    }
`;
const StyledLabel = styled(Label)`
    color: ${({ theme }) => theme.colors.neutral[50]};
`;

const Navigation = styled.div`
    display: flex;
    align-items: center;
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s - 1}px) {
        width: 100%;
    }

    & > * + * {
        margin-left: ${({ theme }) => theme.spacing['40_Standard']};
    }
`;

export const Header = styled.header`
    display: flex;
    grid-column: 1 / -1;
    margin: 4rem 0 2.4rem;
    justify-content: space-between;
    align-items: center;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        flex-flow: column;
        align-items: flex-start;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin: 4rem 0 0;
        height: 4.8rem;

        ${StyledViewToggle} {
            margin-left: auto;
        }
    }
`;
const StyledText = styled.span`
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const SpacingDatePicker = css`
    padding-left: ${({ theme }) => theme.spacing['30_Small']};
    border-left: 1px solid ${({ theme }) => theme.colors.neutral['30']};
    margin-left: 5px;
`;

const StyledDatePickerEditText = styled.span`
    width: 10rem;
    ${SpacingDatePicker}
`;

const StyledDatePickerText = styled.span`
    width: 12rem;
    ${SpacingDatePicker}
`;

const StyledButton = styled(Button)`
    margin-left: 0;
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) and (min-width: ${({ theme }) =>
            theme.mediaQueries.xs}) {
        margin-left: 2.4rem;
    }
`;
